import React from "react";
import { Text } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";

/**
 * Creates a clickable toast content with Text component
 * @param {string} template - The appstr template key (e.g., "Entity has been updated")
 * @param {string|string[]} key - The key(s) in the template to make clickable
 * @param {object} value - Object containing values for replacement and navigation info
 * @param {object} options - Optional configuration
 * @returns {React.Component} - Text component with clickable formatConfig
 * @param {object} textProps - Optional <Text> component props
 */
export const createClickableToast = ({ template, key, value, options = {}, textProps = {} }) => {
  const { color = "#143F1C", customOnClick, keySpecificHandlers = {} } = options; // Green color like the assignment status

  // Handle both single key and array of keys
  const clickableKeys = Array.isArray(key) ? key : [key];

  // Extract the replaceDict and targetID from value
  let replaceDict = {};
  let targetID = null;

  if (typeof value === "object" && value !== null) {
    // If value has targetID, extract it
    if ("targetID" in value) {
      targetID = value.targetID;
      // Create replaceDict without targetID
      replaceDict = Object.keys(value).reduce((acc, k) => {
        if (k !== "targetID") {
          acc[k] = value[k];
        }
        return acc;
      }, {});
    } else {
      replaceDict = value;
    }
  } else {
    // If value is a simple string, use the key as the field name
    replaceDict = { [key]: value };
  }

  const handleClick = e => {
    e.stopPropagation();

    // Use custom onClick if provided
    if (customOnClick) {
      customOnClick(e);
      return;
    }

    // Default navigation behavior
    const wsMgr = GetWSMgr();
    if (targetID) {
      wsMgr.DoNavToBox({ targetID }, true, undefined, true, false);
      ReRenderCanvas();
    }
  };

  // Build formatConfig for clickable keys
  const formatConfig = {};
  Object.keys(replaceDict).forEach(k => {
    if (clickableKeys.includes(k)) {
      // Check if there's a key-specific handler
      const keyHandler = keySpecificHandlers[k] || handleClick;

      formatConfig[k] = {
        type: "bold",
        style: {
          cursor: "pointer",
          textDecoration: "underline",
          color
        },
        onClick: keyHandler
      };
    } else {
      // Non-clickable keys are still bold
      formatConfig[k] = { type: "bold" };
    }
  });

  return (
    <Text
      dynamic={{
        template: appstr(template),
        replaceDict,
        formatConfig
      }}
      {...textProps}
    >
      {appstr(template, replaceDict)}
    </Text>
  );
};
